import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./administrator-form.scss";

const adminRecords = {
  "john-tan": {
    name: "John Tan",
    email: "john@secondbite.my",
    mobile: "012-3456789",
    role: "Super Administrator",
    status: "Active",
    lastLogin: "Today, 9:25 AM",
    createdDate: "12 Jun 2026",
    notes: "Main platform administrator.",
    orderAccess: true,
    vendorAccess: true,
    adminAccess: true,
    settingsAccess: true,
    reportAccess: true,
    sendWelcomeEmail: false,
  },
  "sarah-lim": {
    name: "Sarah Lim",
    email: "sarah@secondbite.my",
    mobile: "016-9988776",
    role: "Operations",
    status: "Active",
    lastLogin: "Yesterday, 5:40 PM",
    createdDate: "14 Jun 2026",
    notes: "Operations team account.",
    orderAccess: true,
    vendorAccess: true,
    adminAccess: false,
    settingsAccess: false,
    reportAccess: true,
    sendWelcomeEmail: false,
  },
  "daniel-wong": {
    name: "Daniel Wong",
    email: "daniel@secondbite.my",
    mobile: "011-2233445",
    role: "Administrator",
    status: "Inactive",
    lastLogin: "12 Jun 2026",
    createdDate: "12 Jun 2026",
    notes: "Inactive admin account for MVP sample.",
    orderAccess: true,
    vendorAccess: true,
    adminAccess: false,
    settingsAccess: false,
    reportAccess: false,
    sendWelcomeEmail: false,
  },
};

const blankAdmin = {
  name: "",
  email: "",
  mobile: "",
  role: "Administrator",
  status: "Active",
  lastLogin: "Never logged in",
  createdDate: "Today",
  notes: "",
  password: "",
  confirmPassword: "",
  orderAccess: true,
  vendorAccess: true,
  adminAccess: false,
  settingsAccess: false,
  reportAccess: true,
  sendWelcomeEmail: true,
};

const slugify = (value) =>
  String(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const AdministratorFormPage = ({ mode = "edit" }) => {
  const navigate = useNavigate();
  const { adminId } = useParams();
  const isCreate = mode === "create";

  const initialAdmin = useMemo(() => {
    if (isCreate) return blankAdmin;
    return adminRecords[adminId] || adminRecords[slugify(adminId || "john-tan")] || adminRecords["john-tan"];
  }, [adminId, isCreate]);

  const [form, setForm] = useState(initialAdmin);
  const [saved, setSaved] = useState(false);

  const updateForm = (field, value) => {
    setSaved(false);
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSaved(true);
  };

  const renderPermission = (label, description, field) => (
    <label className="administrator-form__switch">
      <input
        type="checkbox"
        checked={Boolean(form[field])}
        onChange={(event) => updateForm(field, event.target.checked)}
      />
      <span>
        <strong>{label}</strong>
        <small>{description}</small>
      </span>
    </label>
  );

  return (
    <form className="administrator-form" onSubmit={handleSubmit}>
      <section className="administrator-form__hero">
        <div className="administrator-form__container administrator-form__hero-inner">
          <div>
            <button type="button" className="administrator-form__back" onClick={() => navigate("/admin/administrators")}>
              ← Back to Administrators
            </button>
            <p className="administrator-form__eyebrow">SecondBite Admin</p>
            <h2>{isCreate ? "Add Administrator" : "Edit Administrator"}</h2>
            <p>{isCreate ? "Create a new administrator account and assign access." : "Update administrator account details, role, status, and permissions."}</p>
          </div>

          <button type="submit" className="administrator-form__hero-action">
            {isCreate ? "Create Administrator" : "Save Administrator"}
          </button>
        </div>
      </section>

      {saved && (
        <div className="administrator-form__notice" role="status">
          <div className="administrator-form__container">
            {isCreate ? "Administrator created for MVP preview." : "Administrator updated for MVP preview."} Connect API later.
          </div>
        </div>
      )}

      <section className="administrator-form__summary-wrap">
        <div className="administrator-form__container administrator-form__summary">
          <div className="administrator-form__avatar">{form.name ? form.name.charAt(0).toUpperCase() : "A"}</div>
          <div>
            <h3>{form.name || "New Administrator"}</h3>
            <p>{form.email || "Email not set"}</p>
          </div>
          <span className={`administrator-form__badge administrator-form__badge--${String(form.status).toLowerCase()}`}>
            {form.status}
          </span>
        </div>
      </section>

      <div className="administrator-form__container administrator-form__grid">
        <section className="administrator-form__panel administrator-form__panel--wide">
          <div className="administrator-form__panel-title">
            <h3>Account Information</h3>
            <p>Static MVP form. Connect API later.</p>
          </div>

          <div className="administrator-form__form-grid">
            <label>
              <span>Full Name *</span>
              <input value={form.name} onChange={(event) => updateForm("name", event.target.value)} placeholder="Enter full name" />
            </label>

            <label>
              <span>Email Address *</span>
              <input type="email" value={form.email} onChange={(event) => updateForm("email", event.target.value)} placeholder="name@secondbite.my" />
            </label>

            <label>
              <span>Mobile Number</span>
              <input value={form.mobile} onChange={(event) => updateForm("mobile", event.target.value)} placeholder="012-3456789" />
            </label>

            <label>
              <span>Role</span>
              <select value={form.role} onChange={(event) => updateForm("role", event.target.value)}>
                <option>Super Administrator</option>
                <option>Administrator</option>
                <option>Operations</option>
                <option>Viewer</option>
              </select>
            </label>

            <label>
              <span>Status</span>
              <select value={form.status} onChange={(event) => updateForm("status", event.target.value)}>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </label>

            <label>
              <span>Notes</span>
              <input value={form.notes} onChange={(event) => updateForm("notes", event.target.value)} placeholder="Internal note" />
            </label>
          </div>
        </section>

        <section className="administrator-form__panel">
          <div className="administrator-form__panel-title">
            <h3>Module Permissions</h3>
            <p>Control what this admin can access.</p>
          </div>

          <div className="administrator-form__switch-list">
            {renderPermission("Orders", "View and manage guest checkout orders.", "orderAccess")}
            {renderPermission("Vendors", "Manage vendor, outlet, and product setup.", "vendorAccess")}
            {renderPermission("Administrators", "Create and edit admin accounts.", "adminAccess")}
            {renderPermission("Settings", "Update platform, payment, and email settings.", "settingsAccess")}
            {renderPermission("Reports", "View and export sales reports.", "reportAccess")}
          </div>
        </section>


        <section className="administrator-form__panel">
          <div className="administrator-form__panel-title">
            <h3>Access Summary</h3>
            <p>Quick account status.</p>
          </div>

          <div className="administrator-form__status-list">
            <div>
              <span>Last Login</span>
              <strong>{form.lastLogin}</strong>
            </div>
            <div>
              <span>Created Date</span>
              <strong>{form.createdDate}</strong>
            </div>
            <div>
              <span>Role</span>
              <strong>{form.role}</strong>
            </div>
          </div>
        </section>

        <section className="administrator-form__panel administrator-form__panel--wide">
          <div className="administrator-form__panel-title">
            <h3>{isCreate ? "Temporary Password" : "Reset Password"}</h3>
            <p>{isCreate ? "Set the first login password." : "Leave blank if password is unchanged."}</p>
          </div>

          <div className="administrator-form__form-grid">
            <label>
              <span>{isCreate ? "Temporary Password *" : "New Password"}</span>
              <input
                type="password"
                value={form.password || ""}
                onChange={(event) => updateForm("password", event.target.value)}
                placeholder="Enter password"
              />
            </label>

            <label>
              <span>Confirm Password</span>
              <input
                type="password"
                value={form.confirmPassword || ""}
                onChange={(event) => updateForm("confirmPassword", event.target.value)}
                placeholder="Confirm password"
              />
            </label>

            <label className="administrator-form__switch administrator-form__switch--inside">
              <input
                type="checkbox"
                checked={Boolean(form.sendWelcomeEmail)}
                onChange={(event) => updateForm("sendWelcomeEmail", event.target.checked)}
              />
              <span>
                <strong>Send Welcome Email</strong>
                <small>Email login instructions to the administrator.</small>
              </span>
            </label>
          </div>
        </section>

      </div>

      <div className="administrator-form__footer-wrap">
        <div className="administrator-form__container administrator-form__footer">
          <button type="button" className="administrator-form__secondary" onClick={() => navigate("/admin/administrators")}>
            Cancel
          </button>
          <button type="submit" className="administrator-form__primary">
            {isCreate ? "Create Administrator" : "Save Administrator"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default AdministratorFormPage;
